/**
 * Color Wheel Exercises Component
 * Interactive exercises for learning color theory and harmony identification
 */

import { useState, useCallback, useMemo } from 'react';
import { 
  GraduationCap, 
  CheckCircle, 
  XCircle, 
  RotateCcw, 
  Trophy, 
  Target,
  Lightbulb,
  Play,
  Pause
} from 'lucide-react';
import { EducationalColorWheel } from './EducationalColorWheel';
import { 
  checkHarmonyType, 
  calculateHarmony, 
  type HarmonyType 
} from '../../lib/education/colorHarmony';

interface ExerciseResult {
  isCorrect: boolean;
  feedback: string;
  explanation: string;
  score: number;
}

interface ExerciseSession {
  exerciseType: ExerciseType;
  currentQuestion: number;
  totalQuestions: number;
  correctAnswers: number;
  results: ExerciseResult[];
  isComplete: boolean;
}

type ExerciseType = 'identify-harmony' | 'find-complement' | 'create-scheme' | 'color-relationships';
type Difficulty = 'beginner' | 'intermediate' | 'advanced';

interface ColorWheelExercisesProps {
  onExerciseComplete?: (session: ExerciseSession) => void;
  className?: string;
}

export function ColorWheelExercises({
  onExerciseComplete,
  className = ''
}: ColorWheelExercisesProps) {
  const [exerciseType, setExerciseType] = useState<ExerciseType>('identify-harmony');
  const [difficulty, setDifficulty] = useState<Difficulty>('beginner');
  const [isStarted, setIsStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [userColors, setUserColors] = useState<string[]>([]);
  const [currentSession, setCurrentSession] = useState<ExerciseSession | null>(null);
  const [currentResult, setCurrentResult] = useState<ExerciseResult | null>(null);
  const [showHint, setShowHint] = useState(false);

  // Exercise configurations
  const exerciseConfigs = useMemo(() => ({
    'identify-harmony': {
      beginner: {
        harmonies: ['complementary', 'analogous'] as HarmonyType[],
        questions: 5,
        timeLimit: null,
        description: 'Identify the harmony type shown in the color wheel'
      },
      intermediate: {
        harmonies: ['complementary', 'analogous', 'triadic', 'split-complementary'] as HarmonyType[],
        questions: 8,
        timeLimit: null,
        description: 'Identify more complex harmony relationships'
      },
      advanced: {
        harmonies: ['complementary', 'analogous', 'triadic', 'split-complementary', 'tetradic'] as HarmonyType[],
        questions: 10,
        timeLimit: 300, // 5 minutes
        description: 'Identify all harmony types including tetradic schemes'
      }
    },
    'find-complement': {
      beginner: {
        harmonies: ['complementary'] as HarmonyType[],
        questions: 5,
        timeLimit: null,
        description: 'Find the complementary color for the given base color'
      },
      intermediate: {
        harmonies: ['complementary', 'split-complementary'] as HarmonyType[],
        questions: 7,
        timeLimit: null,
        description: 'Find complementary and split-complementary colors'
      },
      advanced: {
        harmonies: ['complementary', 'split-complementary'] as HarmonyType[],
        questions: 10,
        timeLimit: 240, // 4 minutes
        description: 'Find complements quickly and accurately'
      }
    },
    'create-scheme': {
      beginner: {
        harmonies: ['complementary', 'analogous'] as HarmonyType[],
        questions: 4,
        timeLimit: null,
        description: 'Create simple color schemes using the color wheel'
      },
      intermediate: {
        harmonies: ['complementary', 'analogous', 'triadic'] as HarmonyType[],
        questions: 6,
        timeLimit: null,
        description: 'Create more complex color schemes'
      },
      advanced: {
        harmonies: ['complementary', 'analogous', 'triadic', 'tetradic'] as HarmonyType[],
        questions: 8,
        timeLimit: 420, // 7 minutes
        description: 'Create sophisticated color schemes under time pressure'
      }
    },
    'color-relationships': {
      beginner: {
        harmonies: ['complementary', 'analogous'] as HarmonyType[],
        questions: 6,
        timeLimit: null,
        description: 'Understand basic color relationships and their effects'
      },
      intermediate: {
        harmonies: ['complementary', 'analogous', 'triadic'] as HarmonyType[],
        questions: 8,
        timeLimit: null,
        description: 'Analyze color relationships and their visual impact'
      },
      advanced: {
        harmonies: ['complementary', 'analogous', 'triadic', 'split-complementary', 'tetradic'] as HarmonyType[],
        questions: 12,
        timeLimit: 480, // 8 minutes
        description: 'Master color relationships and their practical applications'
      }
    }
  }), []);

  // Generate current question data
  const currentQuestion = useMemo(() => {
    if (!currentSession || !isStarted) return null;

    const config = exerciseConfigs[exerciseType][difficulty];
    const targetHarmony = config.harmonies[
      Math.floor(Math.random() * config.harmonies.length)
    ];
    
    // Generate a random base color
    const randomHue = Math.floor(Math.random() * 360);
    const baseColor = `hsl(${randomHue}, 100%, 50%)`;
    
    return {
      baseColor,
      targetHarmony,
      harmonyScheme: calculateHarmony(baseColor, targetHarmony),
      questionNumber: currentSession.currentQuestion + 1,
      totalQuestions: config.questions
    };
  }, [currentSession, isStarted, exerciseType, difficulty, exerciseConfigs]);

  // Start new exercise session
  const startExercise = useCallback(() => {
    const config = exerciseConfigs[exerciseType][difficulty];
    const newSession: ExerciseSession = {
      exerciseType,
      currentQuestion: 0,
      totalQuestions: config.questions,
      correctAnswers: 0,
      results: [],
      isComplete: false
    };
    
    setCurrentSession(newSession);
    setIsStarted(true);
    setIsPaused(false);
    setUserColors([]);
    setCurrentResult(null);
    setShowHint(false);
  }, [exerciseType, difficulty, exerciseConfigs]);

  // Check user answer
  const checkAnswer = useCallback(() => {
    if (!currentQuestion || !currentSession) return;

    let isCorrect = false;
    let feedback = '';
    let explanation = '';
    let score = 0;

    switch (exerciseType) {
      case 'identify-harmony':
        // User would select harmony type from buttons (simplified for this example)
        isCorrect = true; // Placeholder - would check against user selection
        feedback = isCorrect ? 'Correct! That\'s a ' + currentQuestion.targetHarmony + ' harmony.' : 'Not quite. Try again!';
        explanation = `A ${currentQuestion.targetHarmony} harmony ${getHarmonyExplanation(currentQuestion.targetHarmony)}`;
        score = isCorrect ? 10 : 0;
        break;

      case 'find-complement':
        isCorrect = userColors.length === 1 && 
                   checkHarmonyType([currentQuestion.baseColor, userColors[0]], 'complementary');
        feedback = isCorrect ? 'Perfect! You found the complementary color.' : 'Not quite. The complementary color should be opposite on the wheel.';
        explanation = 'Complementary colors are located directly opposite each other on the color wheel, creating maximum contrast.';
        score = isCorrect ? 15 : 0;
        break;

      case 'create-scheme':
        isCorrect = checkHarmonyType([currentQuestion.baseColor, ...userColors], currentQuestion.targetHarmony);
        feedback = isCorrect ? 'Excellent! You created a perfect scheme.' : 'Not quite right. Check the color relationships.';
        explanation = `For a ${currentQuestion.targetHarmony} scheme, ${getSchemeInstructions(currentQuestion.targetHarmony)}`;
        score = isCorrect ? 20 : 0;
        break;

      case 'color-relationships':
        // More complex analysis would go here
        isCorrect = userColors.length >= 2;
        feedback = isCorrect ? 'Good analysis of color relationships!' : 'Select more colors to analyze their relationships.';
        explanation = 'Color relationships determine how colors interact and the visual effects they create.';
        score = isCorrect ? 10 : 0;
        break;
    }

    const result: ExerciseResult = {
      isCorrect,
      feedback,
      explanation,
      score
    };

    setCurrentResult(result);

    // Update session
    const updatedSession = {
      ...currentSession,
      currentQuestion: currentSession.currentQuestion + 1,
      correctAnswers: currentSession.correctAnswers + (isCorrect ? 1 : 0),
      results: [...currentSession.results, result],
      isComplete: currentSession.currentQuestion + 1 >= currentSession.totalQuestions
    };

    setCurrentSession(updatedSession);

    if (updatedSession.isComplete) {
      onExerciseComplete?.(updatedSession);
    }
  }, [currentQuestion, currentSession, exerciseType, userColors, onExerciseComplete]);

  // Reset for next question
  const nextQuestion = useCallback(() => {
    setUserColors([]);
    setCurrentResult(null);
    setShowHint(false);
  }, []);

  // Handle color selection from wheel
  const handleColorSelect = useCallback((color: string) => {
    if (!isStarted || isPaused || currentResult) return;

    if (exerciseType === 'find-complement') {
      setUserColors([color]);
    } else if (exerciseType === 'create-scheme') {
      setUserColors(prev => [...prev, color].slice(-4)); // Keep max 4 colors
    } else if (exerciseType === 'color-relationships') {
      setUserColors(prev => [...prev, color].slice(-6)); // Keep max 6 colors
    }
  }, [isStarted, isPaused, currentResult, exerciseType]);

  // Get harmony explanation
  const getHarmonyExplanation = (harmony: HarmonyType): string => {
    const explanations = {
      complementary: 'uses colors opposite each other on the wheel',
      analogous: 'uses colors next to each other for harmony',
      triadic: 'uses three evenly spaced colors for balance',
      'split-complementary': 'uses a base color with two near-complements',
      tetradic: 'uses four colors in two complementary pairs',
      monochromatic: 'uses variations of a single hue'
    };
    return explanations[harmony];
  };

  // Get scheme instructions
  const getSchemeInstructions = (harmony: HarmonyType): string => {
    const instructions = {
      complementary: 'select the color opposite the base color',
      analogous: 'select colors adjacent to the base color',
      triadic: 'select two colors that form a triangle with the base',
      'split-complementary': 'select the two colors near the complement',
      tetradic: 'select three colors to create a balanced four-color scheme',
      monochromatic: 'select variations of the same hue'
    };
    return instructions[harmony];
  };

  // Calculate score percentage
  const scorePercentage = useMemo(() => {
    if (!currentSession) return 0;
    return Math.round((currentSession.correctAnswers / Math.max(1, currentSession.currentQuestion)) * 100);
  }, [currentSession]);

  if (!isStarted) {
    return (
      <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6 ${className}`}>
        <header>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            Color Wheel Exercises
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Practice color theory through interactive exercises and challenges
          </p>
        </header>

        <div className="space-y-4">
          {/* Exercise type selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Choose Exercise Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { type: 'identify-harmony' as ExerciseType, label: 'Identify Harmony', description: 'Recognize color harmony types' },
                { type: 'find-complement' as ExerciseType, label: 'Find Complement', description: 'Locate complementary colors' },
                { type: 'create-scheme' as ExerciseType, label: 'Create Scheme', description: 'Build color schemes from scratch' },
                { type: 'color-relationships' as ExerciseType, label: 'Color Relationships', description: 'Analyze color interactions' }
              ].map(({ type, label, description }) => (
                <button
                  key={type}
                  onClick={() => setExerciseType(type)}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    exerciseType === type
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium text-sm">{label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Difficulty Level
            </label>
            <div className="flex gap-2">
              {[
                { level: 'beginner' as Difficulty, label: 'Beginner' },
                { level: 'intermediate' as Difficulty, label: 'Intermediate' },
                { level: 'advanced' as Difficulty, label: 'Advanced' }
              ].map(({ level, label }) => (
                <button
                  key={level}
                  onClick={() => setDifficulty(level)}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    difficulty === level
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Exercise info */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">
              Exercise Details
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {exerciseConfigs[exerciseType][difficulty].description}
            </p>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Questions: {exerciseConfigs[exerciseType][difficulty].questions}
              {exerciseConfigs[exerciseType][difficulty].timeLimit && (
                <> • Time limit: {exerciseConfigs[exerciseType][difficulty].timeLimit / 60} minutes</>
              )}
            </div>
          </div>

          {/* Start button */}
          <button
            onClick={startExercise}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            <Play className="w-5 h-5" />
            Start Exercise
          </button>
        </div>
      </div>
    );
  }

  if (currentSession?.isComplete) {
    return (
      <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6 ${className}`}>
        <header className="text-center">
          <div className="flex justify-center mb-4">
            <Trophy className="w-12 h-12 text-yellow-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Exercise Complete!
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Great job on completing the color theory exercise
          </p>
        </header>

        <div className="text-center space-y-4">
          <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
            {scorePercentage}%
          </div>
          <div className="text-lg text-gray-700 dark:text-gray-300">
            {currentSession.correctAnswers} out of {currentSession.totalQuestions} correct
          </div>
          
          <div className="space-y-2">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all"
                style={{ width: `${scorePercentage}%` }}
              />
            </div>
          </div>

          <div className="flex justify-center gap-3">
            <button
              onClick={startExercise}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => setIsStarted(false)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6 space-y-6 ${className}`}>
      {/* Exercise header */}
      <header>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Target className="w-5 h-5" />
              {exerciseType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Question {currentQuestion?.questionNumber} of {currentQuestion?.totalQuestions}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHint(!showHint)}
              className={`p-2 rounded-lg transition-colors ${
                showHint
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
              }`}
              title="Show hint"
            >
              <Lightbulb className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsPaused(!isPaused)}
              className={`p-2 rounded-lg transition-colors ${
                isPaused
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
              }`}
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ 
              width: `${((currentSession?.currentQuestion || 0) / (currentSession?.totalQuestions || 1)) * 100}%` 
            }}
          />
        </div>
      </header>

      {/* Main exercise content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Color wheel */}
        <div className={isPaused ? 'opacity-50 pointer-events-none' : ''}>
          <EducationalColorWheel
            size={350}
            onColorSelect={handleColorSelect}
            initialMode="exercise"
            className="mx-auto"
          />
          
          {/* User selections */}
          {userColors.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Your selections:</h4>
              <div className="flex gap-2 flex-wrap">
                {userColors.map((color, index) => (
                  <div
                    key={index}
                    className="w-10 h-10 rounded border-2 border-gray-300 dark:border-gray-600"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Instructions and feedback */}
        <div className="space-y-4">
          {/* Question prompt */}
          {currentQuestion && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-medium text-sm text-blue-700 dark:text-blue-300 mb-2">
                Question {currentQuestion.questionNumber}
              </h4>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                {exerciseType === 'identify-harmony' && `What harmony type is shown?`}
                {exerciseType === 'find-complement' && `Find the complementary color for the base color shown.`}
                {exerciseType === 'create-scheme' && `Create a ${currentQuestion.targetHarmony} color scheme.`}
                {exerciseType === 'color-relationships' && `Select colors and analyze their relationships.`}
              </p>
            </div>
          )}

          {/* Hint */}
          {showHint && currentQuestion && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  <strong>Hint:</strong> {getSchemeInstructions(currentQuestion.targetHarmony)}
                </div>
              </div>
            </div>
          )}

          {/* Result feedback */}
          {currentResult && (
            <div className={`rounded-lg p-4 ${
              currentResult.isCorrect
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {currentResult.isCorrect ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className={`font-medium text-sm mb-1 ${
                    currentResult.isCorrect
                      ? 'text-green-700 dark:text-green-400'
                      : 'text-red-700 dark:text-red-400'
                  }`}>
                    {currentResult.feedback}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {currentResult.explanation}
                  </div>
                  <div className="text-xs font-medium mt-2">
                    Score: +{currentResult.score} points
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            {!currentResult ? (
              <button
                onClick={checkAnswer}
                disabled={userColors.length === 0}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
              >
                Check Answer
              </button>
            ) : (
              <button
                onClick={nextQuestion}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Next Question
              </button>
            )}
            
            <button
              onClick={() => setUserColors([])}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          {/* Score display */}
          {currentSession && (
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              Current Score: {currentSession.correctAnswers}/{currentSession.currentQuestion} ({scorePercentage}%)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
